import type React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { ComboboxOptionObjectInterface, ComboboxOptionType } from '@zillow/constellation-10';
import { Form, Toast, useToast } from '@zillow/constellation-10';
import { SpinnerContainer } from '@zg-rentals/rental-manager-env';
import { TimeZoneDropdownOptions, type TimeZoneIds } from '@zg-rentals/touring';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';
import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import type { PageWizardProps } from '../../Wizard/PageWizard';
import PageWizard from '../../Wizard/PageWizard';
import WizardAlert from '../../WizardAlert';
import TourSchedulingTimeZoneBody from './components/TourSchedulingTimeZoneBody';
import { useTourSchedulingContext } from './context';
import { WizardContext } from '../../Wizard/context';

import { WizardFlexContainer } from '../../../styles/wizard';
import { ActionMetadataEntryPoint } from '@zg-rentals/ts-z-calendar-client';

export type TourSchedulingTimeZoneProps = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: React.ReactNode;
  progressStepper: React.ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: string;
} & PageWizardProps;

const TourSchedulingTimeZone = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: TourSchedulingTimeZoneProps) => {
  const { enqueueToast } = useToast();
  const {
    // @ts-expect-error WizardContext
    wizardState: { isFetchingData },
  } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  const { selectedBookingMethod, selectedTimeZone, setSelectedTimeZone, tourCalendarApiHook } =
    useTourSchedulingContext();

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = async () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    if (selectedBookingMethod && selectedTimeZone) {
      try {
        // If booking method or time zone has not changed, we don't need to call the API
        if (
          selectedBookingMethod !== tourCalendarApiHook.tourConfirmationType ||
          selectedTimeZone !== tourCalendarApiHook.timeZone
        ) {
          await tourCalendarApiHook.enableTouringForListing(
            selectedBookingMethod,
            selectedTimeZone,
            ActionMetadataEntryPoint.NEW_LISTING,
          );
        }
        onGoToNextStep();
      } catch (error) {
        enqueueToast(
          <Toast tone="critical">Your tour setting changes couldn&apos;t be saved. Try again in a few moments.</Toast>,
        );
      }
    }
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }
    onGoToPrevStep();
  };

  const handleTimeZoneChange = useCallback((option: ComboboxOptionType) => {
    const timeZoneId = TimeZoneDropdownOptions.find(
      (timeZoneOption) => timeZoneOption.value === (option as ComboboxOptionObjectInterface).value,
    )?.value;
    setSelectedTimeZone(timeZoneId as TimeZoneIds);
  }, []);

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
      isNextDisabled={!selectedTimeZone || tourCalendarApiHook.isEnableTouringForListingLoading}
    >
      <div data-testid="TourSchedulingTimeZone">
        {tourCalendarApiHook.isGetTourConfigAndAvailabilityLoading ? (
          <SpinnerContainer />
        ) : (
          <>
            <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

            <WizardFlexContainer>
              <Form asChild onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
                <TourSchedulingTimeZoneBody
                  selectedTimeZone={selectedTimeZone}
                  onSelectTimeZone={handleTimeZoneChange}
                />
              </Form>
            </WizardFlexContainer>
          </>
        )}
      </div>
    </PageWizard>
  );
};

export default TourSchedulingTimeZone;
